import { Alert, Button, Form, Select, Space, Typography } from 'antd'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useMemo, useState } from 'react'

import {
  capturePlanningSource,
  getPlanningSources,
} from './capturePlanningSource'
import { PlanningSourceCapture } from './planningTypes'
import { usePlanningSources } from './usePlanningSources'

export const NiiAssessmentSelector = ({
  config,
  readonly,
}: {
  config: PlanningSourceCapture
  readonly: boolean
}) => {
  const { ticketTags, setFieldValues } = useTicketAccess()
  const organization = ticketTags.organization
  const unit = ticketTags.unit
  const group = ticketTags['libra-ticket-group']
  const selectedUnitIds = useMemo(() => (unit ? [unit] : []), [unit])
  const { workflows, entries, loading, workflowError, ticketError } =
    usePlanningSources(organization, group, selectedUnitIds)
  const sources = useMemo(
    () => getPlanningSources(entries, workflows, ticketTags),
    [entries, workflows, ticketTags]
  )
  const [selectedId, setSelectedId] = useState<string>()
  const source =
    sources.find((ticket) => ticket.id === selectedId) ??
    (sources.length === 1 ? sources[0] : undefined)
  const workflow = workflows.find((item) => item.id === source?.workflowId)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  useReportValidation({
    status: 'Error',
    message:
      'Save the NII assessment basis before preparing the inspection plan',
  })

  const save = async () => {
    if (!source || !workflow || readonly || loading || saving) return
    setSaving(true)
    setError(undefined)
    try {
      await setFieldValues(
        capturePlanningSource(
          source,
          workflow,
          config,
          new Date().toISOString()
        )
      )
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Could not save the NII assessment basis'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Space orientation='vertical'>
      <Typography.Title level={4}>NII assessment basis</Typography.Title>
      <Typography.Paragraph>
        Save a completed assessment from this asset and campaign. Its zones,
        recommendations and assessment details will be captured in this plan.
      </Typography.Paragraph>
      {(error || workflowError || ticketError) && (
        <Alert
          type='error'
          showIcon
          title={error ?? 'Could not load NII assessments'}
        />
      )}
      {!group && (
        <Alert
          type='warning'
          title='Create the planning ticket within a campaign to select its NII assessment'
        />
      )}
      {!loading &&
        group &&
        sources.length === 0 &&
        !workflowError &&
        !ticketError && (
          <Alert
            type='info'
            title='No completed NII assessments are available for this asset in this campaign'
          />
        )}
      <Form.Item label='Completed NII assessment'>
        <Select
          aria-label='Completed NII assessment'
          loading={loading}
          disabled={readonly || saving || loading || sources.length < 2}
          placeholder='Select an assessment'
          value={source?.id}
          onChange={setSelectedId}
          options={sources.map((ticket) => ({
            value: ticket.id,
            label: `${ticket.title} · v${ticket.version} · ${ticket.id}`,
          }))}
        />
      </Form.Item>
      <Button
        type='primary'
        onClick={save}
        loading={saving}
        disabled={
          !source ||
          readonly ||
          loading ||
          Boolean(workflowError || ticketError)
        }>
        Save assessment basis
      </Button>
    </Space>
  )
}
